import deleteImageState from '../actions/deleteImageState';
import InlineImageViewState from '../schema/InlineImageViewState';
import ImageStatus from '../schema/ImageStatus';
import { EditorViewState, lazyOperateContent } from 'owa-editor';
import { lazyCacheInlineImage } from 'owa-inline-image-loader';
import { isBrowserIE } from 'owa-user-agent/lib/userAgent';
import { PositionType } from 'roosterjs-editor-types';
import {
    PLACEHOLDER_LOAD_PENDING_ATTRIBUTE_NAME,
    INSERT_OPTION,
    INSERT_INLINEIMAGE_CHANGE_SOURCE,
} from './constants';

const ADD_INLINE_IMAGE_TO_STORE_IE_TIMEOUT_LENGTH = 1000;

export default async function onImageLoad(
    viewState: InlineImageViewState,
    editorViewState: EditorViewState,
    temporaryId: string,
    targetElement: HTMLImageElement
) {
    const operateContent = await lazyOperateContent.import();
    const cacheInlineImage = await lazyCacheInlineImage.import();

    // Disable native image selection on the image element
    targetElement.setAttribute('unselectable', 'on');
    // IE11 still lets you tab to an image that is unselectable. Set tabindex so that the focus moves from the
    // inline image directly to the ribbon.
    targetElement.setAttribute('tabindex', '-1');
    targetElement.style.msUserSelect = 'none';
    targetElement.style.webkitUserSelect = 'none';
    targetElement.onload = null;

    const entry = viewState.imageStatusMap[temporaryId];

    operateContent(editorViewState, (editor, range, domApi) => {
        if (entry?.status == ImageStatus.PlaceholderShown) {
            // If placeholder has been inserted
            const sourceElement = entry.placeholder;
            if (editor.contains(sourceElement)) {
                // If placeholder is found, replace it.
                sourceElement.removeAttribute(PLACEHOLDER_LOAD_PENDING_ATTRIBUTE_NAME);

                editor.replaceNode(sourceElement, targetElement);
            } else {
                // If placeholder is not found, i.e. user has deleted it
                // Directly insert it at cursor.
                editor.insertNode(targetElement, INSERT_OPTION);
            }
        } else {
            // If placeholder has not been inserted, directly insert at cursor.
            editor.insertNode(targetElement, INSERT_OPTION);
        }

        return {
            // After replacing the node to insert the image, move the insertion point so its immediately after the
            // newly inserted image
            selectionRange: domApi.createRange(targetElement, PositionType.After),
            changeSource: INSERT_INLINEIMAGE_CHANGE_SOURCE,
        };
    });

    // Generate and store a dataURI for the copy handler and the reading pane's local lie scenario
    if (isBrowserIE()) {
        // IE triggers onload for images before the image is actually in the DOM
        // so we need to call it from a timer to let the image finish loading.
        setTimeout(function() {
            cacheInlineImage(targetElement);
        }, ADD_INLINE_IMAGE_TO_STORE_IE_TIMEOUT_LENGTH);
    } else {
        cacheInlineImage(targetElement);
    }

    deleteImageState(viewState, temporaryId);
}
